// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import { compile, Scanner, type CompiledAutomaton } from '../domain/ac';
import { buildContext, maskValue, seal, type JwtSummary } from '../domain/mask';
import { classifyFinding, lineStarts, byteOffsetToLineColumn, originalFor, prepareMap, type SourceMapV3 } from '../domain/mappings';
import { extractSources, isIndexMap } from '../domain/indexmap';
import { MalformedMapError } from '../domain/vlq';
import {
  decodeInlineMap,
  extensionOf,
  findSourceMappingURL,
  forbiddenFilename,
  resolveMapPath,
  TEXT_EXTENSIONS,
} from '../domain/smfind';
import { applySourceRoot, safeEntryPath } from '../domain/srcpath';
import { findInjectedEnvObjects, findShapes, CALL_SITE_CHECKS, PLACEHOLDER_NOTE } from '../domain/shapes';
import { readJwt, supabaseRoleAdvice, JWT_CANDIDATE_RE } from '../domain/jwt';
import { PUBLIC_PREFIX_RE, SECRET_KEY_RE } from '../domain/gate';
import type { Match, Note, RecoveredFile, Redacted } from '../types';

/**
 * The per-file scan.
 *
 * Everything here is a pure function of (needles, one file's bytes, the set of paths in the tree),
 * which is what lets it run in a Worker and be tested in Node without a DOM.
 */

export interface NeedleSpec {
  readonly envKey: string;
  readonly variantId: Match['variantId'];
  readonly text: string;
  /** The mask to print for this value — computed on the main thread, never recomputed from bytes. */
  readonly masked: string;
  readonly classification: 'search' | 'search-public' | 'search-conflict';
  readonly jwt?: JwtSummary;
}

export interface PreparedNeedles {
  readonly automaton: CompiledAutomaton;
  readonly specs: readonly NeedleSpec[];
  /** For context scrubbing only — every raw value with its mask. */
  readonly scrubPairs: readonly { value: string; masked: string }[];
}

export function prepareNeedles(specs: readonly NeedleSpec[]): PreparedNeedles {
  const enc = new TextEncoder();
  return {
    automaton: compile(specs.map((s) => enc.encode(s.text))),
    specs,
    scrubPairs: specs.map((s) => ({ value: s.text, masked: s.masked })),
  };
}

export interface FileInput {
  readonly path: string;
  readonly bytes: Uint8Array;
}

export interface FileScan {
  readonly matches: Redacted<Match>[];
  readonly notes: Note[];
  readonly recovered: RecoveredFile[];
  readonly isText: boolean;
  readonly hitCountByNeedle: Map<number, number>;
}

const dec = new TextDecoder('utf-8');
const CHUNK = 1 << 20;

/** Which text asset classes we search. Everything else is counted and named, never silently ignored. */
export function isTextAsset(path: string): boolean {
  const ext = extensionOf(path);
  if (ext === '') return /(^|\/)\.env(\..*)?$/.test(path);
  return TEXT_EXTENSIONS.has(ext);
}

export function isBrotli(path: string): boolean {
  return path.endsWith('.br');
}

/**
 * Scan one already-decompressed file.
 *
 * `allPaths` is needed for the orphan-map check: a `.map` on disk that nothing references is a
 * HIGHER-severity finding, because somebody thought they had hidden it (`--sourcemap hidden`).
 */
export function scanFile(
  input: FileInput,
  needles: PreparedNeedles,
  opts: { referencedMaps: ReadonlySet<string>; hasEnv: boolean },
): FileScan {
  const matches: Redacted<Match>[] = [];
  const notes: Note[] = [];
  const recovered: RecoveredFile[] = [];
  const hitCountByNeedle = new Map<number, number>();

  // The filename pass runs BEFORE any byte is searched. The highest-severity real leak — a .env
  // copied into public/ and shipped verbatim as dist/.env — needs no search at all.
  const forbidden = forbiddenFilename(input.path);
  if (forbidden) {
    notes.push({
      id: forbidden.id,
      severity: 'critical',
      title: forbidden.title,
      detail:
        'Anything under public/ is copied into the build byte for byte. This file is served to anyone who requests it. Delete it from the build and from public/, then rotate everything it contains.',
      path: input.path,
    });
  }

  const text = isTextAsset(input.path);
  if (!text) return { matches, notes, recovered, isText: false, hitCountByNeedle };

  // --- the byte search -------------------------------------------------------------------------
  const scanner = new Scanner(needles.automaton);
  const raw: { spec: NeedleSpec; index: number; end: number; length: number }[] = [];
  for (let off = 0; off < input.bytes.length; off += CHUNK) {
    for (const h of scanner.push(input.bytes.subarray(off, Math.min(off + CHUNK, input.bytes.length)))) {
      const spec = needles.specs[h.patternIndex];
      if (!spec) continue;
      hitCountByNeedle.set(h.patternIndex, (hitCountByNeedle.get(h.patternIndex) ?? 0) + 1);
      raw.push({ spec, index: h.end - h.length, end: h.end, length: h.length });
    }
  }

  // Decode once, lazily, and only if we need it — for context, sourcemaps and the shape pass.
  let decoded: string | null = null;
  const decodeOnce = () => (decoded ??= dec.decode(input.bytes));

  // --- sourcemap discovery ---------------------------------------------------------------------
  let prepared: ReturnType<typeof prepareMap> | null = null;
  let starts: Int32Array | null = null;
  let mapPath: string | null = null;
  let indexMapRefused = false;

  const ext = extensionOf(input.path);
  if (ext !== 'map' && (raw.length > 0 || ext === 'js' || ext === 'mjs' || ext === 'css')) {
    const ref = findSourceMappingURL(decodeOnce());
    if (ref) {
      try {
        if (ref.inline) {
          mapPath = `${input.path} (inline)`;
          const map = JSON.parse(decodeInlineMap(ref.url)) as SourceMapV3;
          ({ prepared, indexMapRefused } = ingestMap(map, mapPath, notes, recovered));
        } else {
          const resolved = resolveMapPath(input.path, ref.url);
          if (resolved === null) {
            notes.push({
              id: 'map-offsite',
              severity: 'info',
              title: 'This file points at a sourcemap somewhere else',
              detail:
                'The sourceMappingURL is an absolute URL, so the map is not in the folder you gave leakmap. leakmap will not fetch it — doing so would send the path of your private build to a third party, which is the exact thing this tool exists to prevent.',
              path: input.path,
            });
          } else if (!opts.referencedMaps.has(resolved)) {
            notes.push({
              id: 'map-missing',
              severity: 'info',
              title: 'A sourcemap is referenced but is not in the folder you gave leakmap',
              detail: `${input.path} points at ${resolved}, which is not in this tree. If you deploy that file, its contents are public whether or not leakmap could see it.`,
              path: input.path,
            });
          }
        }
      } catch (e) {
        notes.push(mapError(input.path, e));
      }
    }
    if (prepared) starts = lineStarts(input.bytes);
  }

  // A `.map` file scanned directly: recover its sources, and note it if nothing references it.
  if (ext === 'map') {
    try {
      const map = JSON.parse(decodeOnce()) as SourceMapV3;
      const r = ingestMap(map, input.path, notes, recovered);
      indexMapRefused = r.indexMapRefused;
      if (!opts.referencedMaps.has(input.path)) {
        notes.push({
          id: 'orphan-map',
          severity: 'high',
          title: 'A sourcemap is here that nothing in the build references',
          detail:
            'This is the shape a `--sourcemap hidden` build produces: the comment is stripped from the JavaScript so the map looks hidden, but the file is still deployed and still readable by anyone who guesses the name. It contains your original source.',
          path: input.path,
        });
      }
    } catch (e) {
      notes.push(mapError(input.path, e));
    }
  }

  // --- turn raw byte hits into masked matches ---------------------------------------------------
  for (const hit of raw) {
    const t = decodeOnce();
    // Byte offsets and string indices differ once the file has non-ASCII, so locate the value in the
    // decoded text by searching near the expected position rather than assuming they line up.
    const approx = t.indexOf(hit.spec.text, Math.max(0, hit.index - 64));
    const strIndex = approx >= 0 ? approx : t.indexOf(hit.spec.text);
    const ctx =
      strIndex >= 0
        ? buildContext({
            text: t,
            matchStart: strIndex,
            matchLength: hit.spec.text.length,
            otherSecrets: needles.scrubPairs.filter((p) => p.value !== hit.spec.text),
          })
        : { beforeRedacted: '', afterRedacted: '' };

    let location: Match['location'];
    let kind: Match['kind'] = 'no-map';
    if (indexMapRefused) {
      location = { outcome: 'refused-index-map', generatedLine: 0, utf16Column: 0 };
      kind = 'build-inlined';
    } else if (prepared && starts) {
      const { generatedLine, utf16Column } = byteOffsetToLineColumn(input.bytes, starts, hit.index);
      const orig = originalFor(prepared, generatedLine, utf16Column);
      const content = orig.sourceIndex !== undefined ? prepared.sourcesContent[orig.sourceIndex] : null;
      const inSource = typeof content === 'string' && content.includes(hit.spec.text);
      kind = classifyFinding(inSource, true);
      location = {
        outcome: orig.outcome,
        generatedLine,
        utf16Column,
        source: orig.source,
        originalLine: orig.originalLine,
        originalColumn: orig.originalColumn,
      };
    }

    matches.push(
      seal({
        envKey: hit.spec.envKey,
        variantId: hit.spec.variantId,
        path: input.path,
        byteOffset: hit.index,
        length: hit.length,
        kind,
        masked: hit.spec.masked,
        beforeRedacted: ctx.beforeRedacted,
        afterRedacted: ctx.afterRedacted,
        location,
      }),
    );
  }

  // --- the fallback tiers ------------------------------------------------------------------------
  const t = decodeOnce();
  for (const shape of findShapes(t)) {
    // Skip anything the user's own .env already explains — a needle match is a better finding.
    if (raw.some((r) => r.spec.text === shape.value)) continue;
    const sev = shape.tier === 'SECRET' ? 'critical' : shape.tier === 'PUBLIC' ? 'expected' : 'medium';
    let jwtSummary: JwtSummary | undefined;
    matches.push(
      seal({
        envKey: shape.label,
        variantId: 'raw',
        ruleId: shape.ruleId,
        path: input.path,
        byteOffset: shape.index,
        length: shape.length,
        kind: 'no-map',
        masked: maskValue(shape.value, jwtSummary),
        beforeRedacted: '',
        afterRedacted: '',
      }),
    );
    notes.push({
      id: `shape-${shape.ruleId}`,
      severity: sev,
      title: `${shape.label} found in ${input.path}`,
      detail: shape.detail ?? `${PLACEHOLDER_NOTE} leakmap has not tested this value against any provider.`,
      path: input.path,
    });
  }

  // JWTs — the whole Supabase discrimination is one claim.
  JWT_CANDIDATE_RE.lastIndex = 0;
  let jm: RegExpExecArray | null;
  while ((jm = JWT_CANDIDATE_RE.exec(t)) !== null) {
    const reading = readJwt(jm[0]);
    if (!reading) continue;
    const advice = supabaseRoleAdvice(reading.summary.role);
    matches.push(
      seal({
        envKey: 'JSON Web Token',
        variantId: 'raw',
        ruleId: 'jwt',
        path: input.path,
        byteOffset: jm.index,
        length: jm[0].length,
        kind: 'no-map',
        masked: maskValue(jm[0], reading.summary),
        beforeRedacted: '',
        afterRedacted: '',
      }),
    );
    if (advice) {
      notes.push({
        id: `jwt-${reading.summary.role}`,
        severity: advice.severity === 'critical' ? 'critical' : 'medium',
        title:
          reading.summary.role === 'service_role'
            ? 'A Supabase service_role key is in your build'
            : 'A Supabase anon key is in your build',
        detail: advice.text,
        path: input.path,
      });
    }
  }

  // The injected env object — reports leaks by variable name with no .env supplied at all.
  if (!opts.hasEnv) {
    const injected = findInjectedEnvObjects(t, (k) => PUBLIC_PREFIX_RE.test(k) || SECRET_KEY_RE.test(k));
    for (const hit of injected) {
      matches.push(
        seal({
          envKey: hit.key,
          variantId: 'raw',
          ruleId: 'injected-env',
          path: input.path,
          byteOffset: hit.index,
          length: hit.valueLength,
          kind: 'build-inlined',
          masked: maskValue(hit.value),
          beforeRedacted: '',
          afterRedacted: '',
        }),
      );
    }
    if (injected.length) {
      notes.push({
        id: 'injected-env-object',
        severity: 'high',
        title: 'Your whole environment object was compiled into this file, variable names and all',
        detail:
          'Reading `import.meta.env` as a whole object rather than one key at a time makes the bundler inline every variable, with the names attached. That is how leakmap can name these without being given your .env.',
        path: input.path,
      });
    }
  }

  for (const check of CALL_SITE_CHECKS) {
    if (!t.includes(check.needle)) continue;
    notes.push({ id: check.id, severity: 'high', title: check.title, detail: check.detail, path: input.path });
  }

  return { matches, notes, recovered, isText: true, hitCountByNeedle };
}

function mapError(path: string, e: unknown): Note {
  // Channel 10 of the leak-channel table: JSON.parse, VLQ and inflate errors love to quote the
  // offending input. No caught error may carry a slice of file content into a note.
  const errorClass = e instanceof MalformedMapError ? 'malformed source map' : e instanceof Error ? e.name : 'error';
  return {
    id: 'map-unreadable',
    severity: 'info',
    title: 'A sourcemap could not be decoded',
    detail: `leakmap could not read the sourcemap for this file (${errorClass}). Any values found in it are still reported; only the original file and line are unavailable.`,
    path,
  };
}

function ingestMap(
  map: SourceMapV3,
  label: string,
  notes: Note[],
  recovered: RecoveredFile[],
): { prepared: ReturnType<typeof prepareMap> | null; indexMapRefused: boolean } {
  // Array.isArray(map.sections) is tested FIRST, before any other field is read. On an index map,
  // `(map.mappings || '').split(';')` yields zero segments and a clean report while the map hands
  // out the entire source tree.
  const indexMapRefused = isIndexMap(map);
  if (indexMapRefused) {
    notes.push({
      id: 'index-map',
      severity: 'medium',
      title: 'Index map — original line numbers refused',
      detail:
        'This is a sourcemap made of sections. leakmap recovers the source from it but will not give you an original file and line, because a partially-correct index-map decoder points at the wrong line silently. Your source is still recoverable below; the byte offset in the built file is exact.',
      path: label,
    });
  }

  for (const s of extractSources(map)) {
    const withRoot = applySourceRoot(map.sourceRoot ?? '', s.source);
    const safe = safeEntryPath(withRoot);
    if (!safe.ok) {
      notes.push({
        id: 'unsafe-source-path',
        severity: 'info',
        title: 'A sourcemap path was skipped as unsafe',
        detail: `One entry in this map has a path leakmap will not write into an archive (${safe.reason}). That is unusual and worth a look on its own.`,
        path: label,
      });
      continue;
    }
    recovered.push({ path: safe.path, content: s.content, fromMap: label });
  }

  if (indexMapRefused) return { prepared: null, indexMapRefused };
  if (typeof map.mappings !== 'string') return { prepared: null, indexMapRefused };
  return { prepared: prepareMap(map), indexMapRefused };
}
